import React, { useState, useEffect, useCallback } from 'react';
import { getDeviceRegistrationStatus } from '../services/dataApi';
import './AddRequisiteModal.css';
import DetailsPortal from './DetailsPortal';

const AddDeviceModal = ({
  isOpen,
  onClose,
  onSubmit,
  onRegistrationComplete,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    userDeviceName: '',
    status: 'active'
  });
  const [userDeviceName, setUserDeviceName] = useState('');
  const [step, setStep] = useState(1);
  const [deviceId, setDeviceId] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => {
    if (!loading) {
      setFormData({
        userDeviceName: '',
        status: 'active'
      });
      setUserDeviceName('');
      setStep(1);
      setDeviceId('');
      setSecondsLeft(30);
      setError(null);
      onClose();
    }
  }, [loading, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isEdit && initialData) {
      setFormData({
        userDeviceName: initialData.userDeviceName || initialData.UserDeviceName || initialData.user_device_name || '',
        status: initialData.status || initialData.Status || 'active'
      });
      return;
    }

    setUserDeviceName('');
    setStep(1);
    setDeviceId('');
    setSecondsLeft(30);
    setError(null);
  }, [isOpen, initialData, isEdit]);

  useEffect(() => {
    if (!isOpen || isEdit || step !== 2) {
      return;
    }

    let active = true;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const pollInterval = setInterval(async () => {
      if (!deviceId || !active) {
        return;
      }
      try {
        const result = await getDeviceRegistrationStatus(deviceId);
        if (result.success && result.status === 'completed') {
          onRegistrationComplete?.();
          handleClose();
        }
      } catch (err) {
        setError(err.message || 'Failed to check registration status');
      }
    }, 2000);

    return () => {
      active = false;
      clearInterval(interval);
      clearInterval(pollInterval);
    };
  }, [isOpen, isEdit, step, deviceId, onRegistrationComplete, handleClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleStartRegistration = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!userDeviceName.trim()) {
        setError('Enter a device label');
        setLoading(false);
        return;
      }

      const result = await onSubmit({ deviceName: userDeviceName.trim() });

      if (result.success) {
        setDeviceId(result.deviceId);
        setSecondsLeft(30);
        setStep(2);
        setLoading(false);
      } else {
        setError(result.error || 'Failed to start registration');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!formData.userDeviceName) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      const result = await onSubmit({
        userDeviceName: formData.userDeviceName,
        status: formData.status
      }, initialData);

      if (result.success) {
        setFormData({
          userDeviceName: '',
          status: 'active'
        });
        setLoading(false);
        onClose();
      } else {
        setError(result.error || 'Failed to update device');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onRegistrationComplete?.();
    handleClose();
  };

  if (!isOpen) return null;

  const qrUrl = deviceId
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(deviceId)}`
    : '';

  return (
    <DetailsPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Device' : (step === 1 ? 'Add New Device' : 'Register Device')}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {isEdit ? (
          <form onSubmit={handleEditSubmit} className="requisite-form">
            <div className="form-group">
              <label htmlFor="userDeviceName">
                Device Label <span className="required">*</span>
              </label>
              <input
                type="text"
                id="userDeviceName"
                name="userDeviceName"
                value={formData.userDeviceName}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">
                Status <span className="required">*</span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleStartRegistration} className="requisite-form">
            {step === 1 ? (
              <>
                <div className="form-group">
                  <label htmlFor="userDeviceName">
                    Device label <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="userDeviceName"
                    name="userDeviceName"
                    value={userDeviceName}
                    onChange={(e) => {
                      setUserDeviceName(e.target.value);
                      setError(null);
                    }}
                    required
                    disabled={loading}
                    className="form-input"
                  />
                </div>

                {error && (
                  <div className="form-error">
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Next'}
                  </button>
                </div>
              </>
            ) : (
              <div className="device-register-step">
                <div className="device-register-info">
                  <p>Scan the QR code to register the device.</p>
                  <div className="device-register-id">
                    ID: <span>{deviceId}</span>
                  </div>
                </div>
                {qrUrl && (
                  <div className="device-register-qr">
                    <img src={qrUrl} alt="Device QR code" />
                  </div>
                )}
                <div className={`device-register-timer ${secondsLeft === 0 ? 'expired' : ''}`}>
                  {secondsLeft > 0 ? `Time left: ${secondsLeft}s` : 'Expired'}
                </div>
                {error && (
                  <div className="form-error">
                    {error}
                  </div>
                )}
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setDeviceId('');
                      setSecondsLeft(30);
                      setError(null);
                    }}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Start over
                  </button>
                  <button
                    type="button"
                    onClick={handleFinish}
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </form>
          )}
        </div>
      </div>
    </DetailsPortal>
  );
};

export default AddDeviceModal;
